import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Keep third-party / async errors (e.g. the external AVA orb widget on iOS Safari) from
// crashing the app or triggering the full-screen dev error overlay. Log, don't blow up.
const isExternalNoise = (src, msg) => {
  const s = `${src || ""} ${msg || ""}`;
  return /ava-neural-dock|orb\.js|hive\/orb|ResizeObserver|Script error/i.test(s);
};
window.addEventListener("error", (e) => {
  if (isExternalNoise(e?.filename, e?.message)) {
    // eslint-disable-next-line no-console
    console.warn("Suppressed non-fatal external error:", e?.message);
    e.preventDefault();
    e.stopImmediatePropagation();
    return true;
  }
}, true);
window.addEventListener("unhandledrejection", (e) => {
  const reason = e?.reason;
  if (isExternalNoise(reason?.stack, reason?.message || String(reason))) {
    console.warn("Suppressed non-fatal external rejection:", reason?.message || reason);
    e.preventDefault();
  }
});

// Guarantee & strengthen microphone noise-cancellation for the AVA voice-live orb.
// Runs before the orb injects, so every mic capture (getUserMedia audio) is forced to use
// echo-cancellation, noise-suppression, auto-gain, plus voice-isolation / advanced filters
// where the browser supports them (unknown keys are safely ignored by browsers that don't).
(function enforceVoiceNoiseCancellation() {
  const md = navigator.mediaDevices;
  if (!md || !md.getUserMedia) return;
  const orig = md.getUserMedia.bind(md);
  const NC = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    voiceIsolation: true,        // Safari 16.4+ / experimental Chromium
    googEchoCancellation: true,  // legacy Chromium hints
    googNoiseSuppression: true,
    googAutoGainControl: true,
    googHighpassFilter: true,
  };
  md.getUserMedia = function (constraints) {
    try {
      if (constraints && constraints.audio) {
        const a = constraints.audio === true ? {} : { ...constraints.audio };
        constraints = { ...constraints, audio: { ...a, ...NC } };
      }
    } catch (_) { /* fall back to the original request */ }
    return orig(constraints);
  };
})();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);

// Register service worker for offline support (brain cache + app shell)
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}
